import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import ViewEditDealModal from './ViewEditDealModal';
import EnhancedRefundModal from './EnhancedRefundModal';
import { useNotification } from '../common';

// Dynamic color mapping will come from the stages data from the API

const PipelineView = ({ filters = {}, onDealCreated }) => {
  // Import useNotification hook
  const { showNotification } = useNotification();
  
  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [isViewEditModalOpen, setIsViewEditModalOpen] = useState(false);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [dropTargetStage, setDropTargetStage] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundDealId, setRefundDealId] = useState(null);
  const [refundStageId, setRefundStageId] = useState(null);
  const [refundCourseId, setRefundCourseId] = useState(null);
  // We don't need local filter UI since it's passed from the parent component
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  
  // Reference to the confetti canvas container
  const confettiRef = useRef(null);
  
  // Remove the auto-hide success message effect as it's now handled by NotificationContainer
  
  // Use filters directly from props without local state
  const activeFilters = filters;
  
  // Function to trigger confetti
  const triggerConfetti = () => {
    if (confettiRef.current) {
      const canvas = confettiRef.current;
      const myConfetti = confetti.create(canvas, {
        resize: true,
        useWorker: true
      });
      
      myConfetti({
        particleCount: 200,
        spread: 160,
        origin: { y: 0.6 },
        colors: ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff']
      });
    }
  };
  
  // Fetch users for filters
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/auth/users', config);
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };
  
  // Fetch courses for filters
  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/settings/courses/active', config);
      setCourses(response.data);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };
  
  // Fetch pipeline stages and deals
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication token not found');
          setLoading(false);
          return;
        }
        
        // Log filters for debugging
        console.log('Pipeline view filters:', activeFilters);
        
        const config = {
          headers: {
            'x-auth-token': token
          },
          params: {
            ...activeFilters,
            limit: 100000 // Set a much higher limit to get all deals
          }
        };
        
        // Fetch pipeline stages and deals in parallel
        const [stagesRes, dealsRes, usersRes, coursesRes] = await Promise.all([
          axios.get('/api/deals/stages/all', {
            headers: { 'x-auth-token': token }
          }),
          axios.get('/api/deals', config),
          axios.get('/api/auth/users', {
            headers: { 'x-auth-token': token }
          }),
          axios.get('/api/settings/courses/active', {
            headers: { 'x-auth-token': token }
          })
        ]);
        
        setStages(stagesRes.data);
        // Check if dealsRes.data is an object with a 'deals' property (paginated response)
        // or if it's an array (old API response format)
        if (dealsRes.data && dealsRes.data.deals && Array.isArray(dealsRes.data.deals)) {
          setDeals(dealsRes.data.deals);
        } else if (Array.isArray(dealsRes.data)) {
          setDeals(dealsRes.data);
        } else {
          // Fallback to empty array if data is in unexpected format
          console.error('Unexpected deals data format:', dealsRes.data);
          setDeals([]);
        }
        
        setUsers(usersRes.data);
        setCourses(coursesRes.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching pipeline data:', err);
        setError('Failed to load pipeline: ' + (err.response?.data?.message || err.message));
        setLoading(false);
      }
    };
    
    fetchData();
  }, [activeFilters]);
  
  // We removed all local filter handling since filters are passed from parent
  
  // Set a limit for visible deals per stage
  const INITIAL_DEAL_LIMIT = 15;
  const [stageVisibleLimits, setStageVisibleLimits] = useState({});
  
  // Listen for deal created events from other components
  useEffect(() => {
    const handleDealCreated = (event) => {
      // Show notification if we can get the deal data from the event
      if (event.detail && event.detail.title) {
        showNotification(`Deal "${event.detail.title}" created successfully`, 'success');
      }
      
      // Refresh the pipeline data when a new deal is created
      const refreshData = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) return;
          
          const config = {
            headers: {
              'x-auth-token': token
            },
            params: activeFilters
          };
          
          const dealsRes = await axios.get('/api/deals', config);
          
          if (dealsRes.data && dealsRes.data.deals && Array.isArray(dealsRes.data.deals)) {
            setDeals(dealsRes.data.deals);
          } else if (Array.isArray(dealsRes.data)) {
            setDeals(dealsRes.data);
          }
        } catch (err) {
          console.error('Error refreshing deals:', err);
        }
      };
      
      refreshData();
    };
    
    // Add event listener for custom dealCreated event
    window.addEventListener('dealCreated', handleDealCreated);
    
    // Cleanup
    return () => {
      window.removeEventListener('dealCreated', handleDealCreated);
    };
  }, [activeFilters]);
  
  // Initialize visible limits for each stage
  useEffect(() => {
    if (stages.length > 0) {
      const initialLimits = {};
      stages.forEach(stage => {
        initialLimits[stage.id] = INITIAL_DEAL_LIMIT;
      });
      setStageVisibleLimits(initialLimits);
    }
  }, [stages]);
  
  // Function to load more deals for a specific stage
  const loadMoreDeals = (stageId) => {
    setStageVisibleLimits(prev => ({
      ...prev,
      [stageId]: (prev[stageId] || INITIAL_DEAL_LIMIT) + 15
    }));
  };
  
  // Function to get deals by stage with a limit applied
  const getDealsByStage = (stageId) => {
    const stageDeals = deals.filter(deal => parseInt(deal.stage_id) === parseInt(stageId));
    // Sort deals by creation date, newest first
    return stageDeals.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA; // Newest first (descending order)
    });
  };
  
  // Function to get visible deals by stage (with limit)
  const getVisibleDealsByStage = (stageId) => {
    const stageDeals = getDealsByStage(stageId);
    const limit = stageVisibleLimits[stageId] || INITIAL_DEAL_LIMIT;
    return stageDeals.slice(0, limit);
  };
  
  // Function to get color class based on stage object with color from database
  const getColorClass = (stage) => {
    // If the stage has a custom color defined in the database, use it
    if (stage.color) {
      // Convert the color to a very light background (15% opacity)
      const bgColor = hexToRgba(stage.color, 0.15);
      return {
        backgroundColor: bgColor,
        borderRadius: '0.5rem 0.5rem 0 0',
      };
    }
    
    // Fallback to default colors based on stage type
    if (stage.is_won) {
      return 'bg-green-100 rounded-t-lg';
    } else if (stage.is_lost) {
      return 'bg-red-100 rounded-t-lg';
    } else {
      return 'bg-gray-100 rounded-t-lg';
    }
  };
  
  // Helper function to convert hex color to rgba
  const hexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(200, 200, 200, ${alpha})`;
    
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    if (!result) return `rgba(200, 200, 200, ${alpha})`;
    
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  // Function to format currency
  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    
    const formatOptions = {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    };
    
    return new Intl.NumberFormat('en-US', formatOptions).format(amount);
  };
  
  const handleViewDeal = (dealId) => {
    setSelectedDealId(dealId);
    setIsViewEditModalOpen(true);
  };
  
  const handleDealUpdated = (updatedDeal) => {
    setDeals(prevDeals => 
      prevDeals.map(deal => 
        deal.id === updatedDeal.id ? updatedDeal : deal
      )
    );
  };
  
  // Handle drag start
  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    
    // Create a ghost image with the deal card
    const ghostElement = e.currentTarget.cloneNode(true);
    ghostElement.style.position = 'absolute';
    ghostElement.style.top = '-1000px';
    ghostElement.style.opacity = '0.5';
    
    document.body.appendChild(ghostElement);
    e.dataTransfer.setDragImage(ghostElement, 20, 20);
    
    // Remove the ghost element after the drag starts
    setTimeout(() => {
      document.body.removeChild(ghostElement);
    }, 0);
    
    e.dataTransfer.setData('text/plain', deal.id);
    e.currentTarget.classList.add('dragging');
  };
  
  // Handle drag over
  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    setDropTargetStage(stageId);
  };
  
  // Handle drag end
  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
    
    // Reset drag state
    setTimeout(() => {
      setDraggedDeal(null);
      setDropTargetStage(null);
    }, 50);
  };
  
  // Handle drop
  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    
    if (!draggedDeal || draggedDeal.stage_id === stageId) {
      return;
    }
    
    // Get the stage name to check for special handling
    const targetStage = stages.find(stage => stage.id === stageId);
    if (!targetStage) return;
    
    // Special handling for Refunded stage - show refund modal
    if (targetStage.name === 'Refunded') {
      setRefundDealId(draggedDeal.id);
      setRefundStageId(stageId);
      setRefundCourseId(draggedDeal.course_id || null);
      setShowRefundModal(true);
      return; // Exit early, will continue after refund amount is entered
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.patch(
        '/api/deals/' + draggedDeal.id + '/stage',
        { stage_id: stageId },
        config
      );
      
      // Update local state
      const updatedDeal = res.data;
      setDeals(prevDeals => 
        prevDeals.map(deal => 
          deal.id === updatedDeal.id ? updatedDeal : deal
        )
      );
      
      // Show appropriate success message
      if (targetStage.is_won) {
        triggerConfetti();
        showNotification(`🎉 Deal moved to ${targetStage.name}!`, 'success');
      } else {
        showNotification(`Deal moved to ${targetStage.name}`, 'success');
      }
    } catch (err) {
      console.error('Error updating deal stage:', err);
    }
    
    // Reset drag state
    setDraggedDeal(null);
    setDropTargetStage(null);
  };
  
  // Handle refund confirmation - now mostly handled by the EnhancedRefundModal component
  const handleRefundConfirm = async (refundAmount) => {
    try {
      // With the enhanced refund flow, all refund details are processed by the refund API
      // We just need to update the local state with the updated deal
      
      // Fetch the updated deal
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Get the updated deal
      const res = await axios.get('/api/deals/' + refundDealId, config);
      
      // Update local state
      const updatedDeal = res.data;
      setDeals(prevDeals => 
        prevDeals.map(deal => 
          deal.id === updatedDeal.id ? updatedDeal : deal
        )
      );
      
      // Show success message
      showNotification(`Deal refunded with amount ${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: updatedDeal.currency || 'USD',
      }).format(refundAmount)}`, 'success');
      
      // Close modal and reset state
      setShowRefundModal(false);
    } catch (err) {
      console.error('Error updating deal stage with refund:', err);
      showNotification('Failed to update deal stage. Please try again.', 'error');
      setShowRefundModal(false);
    }
    
    // Reset refund state
    setRefundDealId(null);
    setRefundStageId(null);
    
    // Reset drag state
    setDraggedDeal(null);
    setDropTargetStage(null);
  };
  
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p>Loading pipeline data...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center text-red-600">
        <p>{error}</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow relative">
      {/* Hidden canvas for confetti */}
      <canvas 
        ref={confettiRef} 
        className="fixed inset-0 pointer-events-none z-50"
        style={{ width: '100%', height: '100%' }}
      ></canvas>
      
      <div className="p-6 border-b border-gray-200 relative">
        <div>
          <h3 className="text-lg font-semibold">Deal Pipeline</h3>
          <p className="text-gray-500 mt-1">Drag deals between stages to update their status</p>
        </div>
        
        {/* Success messages now handled by NotificationContainer */}
      </div>
      
      <div className="p-4 overflow-x-auto" style={{ height: 'calc(100vh - 290px)' }}>
        <div className="flex gap-4 min-w-max pb-4 h-full">
          {stages.sort((a, b) => a.display_order - b.display_order).map(stage => (
            <div key={stage.id} className="w-72 flex flex-col h-full">
              <div 
                className={typeof getColorClass(stage) === 'string' 
                  ? `p-3 ${getColorClass(stage)}` 
                  : 'p-3'}
                style={typeof getColorClass(stage) === 'object' ? getColorClass(stage) : {}}
              >
                <div className="flex justify-between items-center">
                  {/* Add shadow to text to ensure readability on any background */}
                  <h4 className="font-medium text-gray-800 drop-shadow-sm">{stage.name}</h4>
                  <span className="text-sm bg-white rounded-full px-2 py-0.5 shadow-sm">
                    {getDealsByStage(stage.id).length}
                  </span>
                </div>
                <div className="mt-1 text-xs font-medium text-gray-700 drop-shadow-sm">
                  {stage.name === 'Refunded' 
                    ? `Total Refunded: ${formatCurrency(
                        getDealsByStage(stage.id).reduce((sum, deal) => {
                          // Calculate the refunded amount in company currency using the same approach as deal value
                          if (deal.refunded_amount) {
                            // Check if we have company currency value first
                            if (deal.refunded_amount_companycurrency) {
                              return sum + parseFloat(deal.refunded_amount_companycurrency);
                            }
                            // If no company currency value for refund specifically, calculate conversion
                            // using the same ratio as original value conversion
                            else if (deal.value && deal.value_companycurrency && deal.value > 0) {
                              const ratio = parseFloat(deal.value_companycurrency) / parseFloat(deal.value);
                              return sum + (parseFloat(deal.refunded_amount) * ratio);
                            }
                            // As a last resort, just use the refunded amount
                            return sum + parseFloat(deal.refunded_amount);
                          }
                          return sum;
                        }, 0),
                        'NOK'
                      )}`
                    : `Total: ${formatCurrency(
                        getDealsByStage(stage.id).reduce((sum, deal) => {
                          return sum + (parseFloat(deal.value_companycurrency) || parseFloat(deal.value) || 0);
                        }, 0),
                        'NOK'
                      )}`
                  }
                </div>
              </div>
              
              <div 
                className={`bg-gray-50 rounded-b-lg p-2 flex-1 overflow-y-auto transition-colors duration-200 ${
                  dropTargetStage === stage.id ? 'bg-blue-50 border-2 border-dashed border-blue-400' : 'shadow-sm'
                }`}
                onDragOver={e => handleDragOver(e, stage.id)}
                onDrop={e => handleDrop(e, stage.id)}
              >
                {getVisibleDealsByStage(stage.id).map(deal => (
                  <div 
                    key={deal.id} 
                    className={`bg-white rounded-lg p-3 mb-2 border ${
                      draggedDeal && draggedDeal.id === deal.id 
                        ? 'border-blue-400 shadow-md opacity-50' 
                        : 'border-gray-200 shadow'
                    } cursor-grab hover:shadow-md transition-shadow`}
                    draggable="true"
                    onDragStart={e => handleDragStart(e, deal)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleViewDeal(deal.id)}
                  >
                    <div className="font-medium text-blue-800 mb-1 whitespace-pre-wrap">{deal.title ? deal.title.replace(/\//g, "/\n") : ''}</div>
                    <div className="text-sm text-gray-600">{deal.lead_name || 'No client'}</div>
                    <div className="text-xs text-gray-500 mt-1">{deal.course || '-'}</div>
                    
                    {/* Date information */}
                    <div className="flex flex-col mt-2 space-y-1 mb-1">
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-500">
                          Created: {deal.created_at ? new Date(deal.created_at).toLocaleDateString() : '-'}
                        </div>
                        <div className="font-semibold text-gray-800">
                          {formatCurrency(deal.value, deal.currency)}
                        </div>
                      </div>
                      
                      {/* Payment date - only show if it exists */}
                      {deal.paid_date && (
                        <div className="text-xs text-green-600 font-medium">
                          Payment received: {new Date(deal.paid_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    {/* Show refunded amount if in Refunded stage */}
                    {stage.name === 'Refunded' && deal.refunded_amount && (
                      <div className="mt-1 text-sm text-red-600">
                        Refunded: {formatCurrency(deal.refunded_amount, deal.currency)}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* "Load More" button - only show if there are more deals to load */}
                {getDealsByStage(stage.id).length > (stageVisibleLimits[stage.id] || INITIAL_DEAL_LIMIT) && (
                  <button
                    onClick={() => loadMoreDeals(stage.id)}
                    className="w-full py-2 mt-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Load more ({getDealsByStage(stage.id).length - (stageVisibleLimits[stage.id] || INITIAL_DEAL_LIMIT)} remaining)
                  </button>
                )}
                
                {getDealsByStage(stage.id).length === 0 && !dropTargetStage && (
                  <div className="h-full flex items-center justify-center text-gray-400 italic text-sm">
                    No deals in this stage
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* View/Edit Deal Modal */}
      <ViewEditDealModal
        isOpen={isViewEditModalOpen}
        onClose={() => setIsViewEditModalOpen(false)}
        onDealUpdated={handleDealUpdated}
        onDealCreated={onDealCreated ? 
          (newDeal) => {
            // Update local state
            setDeals(prevDeals => [newDeal, ...prevDeals]);
            // Also notify parent component
            onDealCreated(newDeal);
          } : undefined
        }
        dealId={selectedDealId}
      />

      {/* Refund Modal */}
      <EnhancedRefundModal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundDealId(null);
          setRefundStageId(null);
          setRefundCourseId(null);
          setDraggedDeal(null);
          setDropTargetStage(null);
        }}
        onConfirm={handleRefundConfirm}
        dealId={refundDealId}
        dealValue={deals.find(d => d.id === refundDealId)?.value || 0}
        dealCurrency={deals.find(d => d.id === refundDealId)?.currency || 'USD'}
        courseId={refundCourseId}
      />
    </div>
  );
};

export default PipelineView;